import React, { useState, useCallback } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, ActivityIndicator, RefreshControl } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import { ClubsStackParamList } from '../../types/navigation';
import ThemedText from '../common/ThemedText';
import ReferralRequestCard from './ReferralRequestCard';
import {
  getReferralRequests,
  getMyReferralRequests,
  getMyIntros,
  getLeaderboard,
  ReferralRequest,
  ReferralResponse,
  LeaderboardEntry,
} from '../../lib/api/referrals';
import { Image } from 'expo-image';

type SubTab = 'requests' | 'my-requests' | 'my-intros' | 'leaderboard';

interface ClubReferralsTabProps {
  clubId: string;
  allowedPostingDays?: number[];
  onCreateReferral: () => void;
}

const ClubReferralsTab: React.FC<ClubReferralsTabProps> = ({ clubId, allowedPostingDays = [], onCreateReferral }) => {
  const { theme } = useTheme();
  const navigation = useNavigation<StackNavigationProp<ClubsStackParamList>>();
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('requests');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const [requests, setRequests] = useState<ReferralRequest[]>([]);
  const [myRequests, setMyRequests] = useState<ReferralRequest[]>([]);
  const [myIntros, setMyIntros] = useState<ReferralResponse[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);

  // Check if posting is allowed today
  const currentDay = new Date().getDay();
  const isPostingAllowedToday = allowedPostingDays.length === 0 || allowedPostingDays.includes(currentDay);

  // Get day names for banner
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const allowedDayNames = allowedPostingDays.length > 0 
    ? allowedPostingDays.map(d => dayNames[d]).join(', ')
    : null;

  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [clubId, activeSubTab])
  );

  const loadData = async () => {
    try {
      setLoading(true);
      
      switch (activeSubTab) {
        case 'requests':
          const requestsData = await getReferralRequests(clubId);
          setRequests(requestsData);
          break;
        case 'my-requests':
          const myRequestsData = await getMyReferralRequests(clubId);
          setMyRequests(myRequestsData);
          break;
        case 'my-intros':
          const myIntrosData = await getMyIntros(clubId);
          setMyIntros(myIntrosData);
          break;
        case 'leaderboard':
          const leaderboardData = await getLeaderboard(clubId);
          setLeaderboard(leaderboardData);
          break;
      }
    } catch (error) {
      console.error('Error loading referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleSubmitIntro = (requestId: string) => {
    navigation.navigate('SubmitIntro', { requestId, clubId });
  };

  const renderSubTabs = () => {
    const tabs: { key: SubTab; label: string; icon: string }[] = [
      { key: 'requests', label: 'Bintros', icon: 'megaphone-outline' },
      { key: 'my-requests', label: 'My Leads', icon: 'document-text-outline' },
      { key: 'my-intros', label: 'Connects', icon: 'hand-right-outline' },
      // { key: 'leaderboard', label: 'Leaderboard', icon: 'trophy-outline' },
    ];

    return (
      <View style={[styles.subTabContainer, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
        <View style={styles.subTabScroll}>
          {tabs.map((tab) => {
            const isActive = activeSubTab === tab.key;
            return (
              <TouchableOpacity
                key={tab.key}
                style={[
                  styles.subTab,
                  isActive && { borderBottomColor: theme.accent, borderBottomWidth: 2 }
                ]}
                onPress={() => setActiveSubTab(tab.key)}
                activeOpacity={0.7}
              >
                <Ionicons
                  name={tab.icon as any}
                  size={18}
                  color={isActive ? theme.accent : theme.textSecondary}
                />
                <ThemedText
                  style={[
                    styles.subTabLabel,
                    { color: isActive ? theme.accent : theme.textSecondary },
                    isActive && styles.subTabLabelActive
                  ]}
                >
                  {tab.label}
                </ThemedText>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  const renderRequests = () => {
    if (loading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      );
    }

    if (requests.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Ionicons name="megaphone-outline" size={64} color={theme.textTertiary} />
          <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
            No leads yet
          </ThemedText>
          <ThemedText style={[styles.emptySubtext, { color: theme.textTertiary }]}>
            Be the first to post a lead!
          </ThemedText>
        </View>
      );
    }

    return (
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.accent} />
        }
      >
        {requests.map((request) => (
          <ReferralRequestCard
            key={request.id}
            request={request}
            onPress={() => {}}
            showSubmitButton
            onSubmitIntro={() => handleSubmitIntro(request.id)}
          />
        ))}
      </ScrollView>
    );
  };

  const renderMyRequests = () => {
    if (loading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      );
    }

    if (myRequests.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Ionicons name="document-text-outline" size={64} color={theme.textTertiary} />
          <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
            You haven't posted any leads
          </ThemedText>
          <TouchableOpacity
            style={[styles.createButton, { backgroundColor: theme.accent }]}
            onPress={onCreateReferral}
            activeOpacity={0.7}
          >
            <Ionicons name="add-circle-outline" size={20} color={theme.textInverse} />
            <ThemedText style={[styles.createButtonText, { color: theme.textInverse }]}>
              Create Lead
            </ThemedText>
          </TouchableOpacity>
        </View>
      );
    }

    // Calculate stats
    const totalIntrosReceived = myRequests.reduce((sum, req) => sum + (req.responses?.length || 0), 0);
    const totalIntrosAccepted = myRequests.reduce((sum, req) => {
      const acceptedCount = req.responses?.filter(r => r.status === 'accepted').length || 0;
      return sum + acceptedCount;
    }, 0);
    const totalIntrosPending = myRequests.reduce((sum, req) => {
      const pendingCount = req.responses?.filter(r => r.status === 'pending').length || 0;
      return sum + pendingCount;
    }, 0);

    return (
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.accent} />
        }
      >
        {/* Stats Bar */}
        <View style={[styles.statsBar, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: theme.accent }]}>
              {myRequests.length}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              My Leads
            </ThemedText>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: '#F59E0B' }]}>
              {totalIntrosReceived}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              {totalIntrosReceived === 1 ? 'Intro Received' : 'Intros Received'}
            </ThemedText>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: '#EF4444' }]}>
              {totalIntrosPending}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              {totalIntrosPending === 1 ? 'Pending Intro' : 'Pending Intros'}
            </ThemedText>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: '#10B981' }]}>
              {totalIntrosAccepted}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              {totalIntrosAccepted === 1 ? 'Intro Accepted' : 'Intros Accepted'}
            </ThemedText>
          </View>
        </View>

        {myRequests.map((request) => (
          <ReferralRequestCard
            key={request.id}
            request={request}
            onPress={() => navigation.navigate('ReferralRequestDetail', { requestId: request.id, clubId })}
            showEditButton={request.status === 'open'}
            onEdit={() => navigation.navigate('CreateReferral', { clubId, requestId: request.id })}
            showIntrosReceived={true}
          />
        ))}
      </ScrollView>
    );
  };

  const renderMyIntros = () => {
    if (loading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      );
    }

    if (myIntros.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Ionicons name="hand-right-outline" size={64} color={theme.textTertiary} />
          <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
            You haven't submitted any Bintros
          </ThemedText>
          <ThemedText style={[styles.emptySubtext, { color: theme.textTertiary }]}>
            Help others by submitting business introductions!
          </ThemedText>
        </View>
      );
    }

    // Calculate stats
    const totalIntrosSent = myIntros.length;
    const totalIntrosAccepted = myIntros.filter(intro => intro.status === 'accepted').length;

    return (
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.accent} />
        }
      >
        {/* Stats Bar */}
        <View style={[styles.statsBar, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: theme.accent }]}>
              {totalIntrosSent}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Bintros Sent
            </ThemedText>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: '#10B981' }]}>
              {totalIntrosAccepted}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Bintros Accepted
            </ThemedText>
          </View>
          <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
          <View style={styles.statItem}>
            <ThemedText style={[styles.statValue, { color: '#EF4444' }]}>
              {totalIntrosSent - totalIntrosAccepted}
            </ThemedText>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Pending
            </ThemedText>
          </View>
        </View>
        {myIntros.map((intro) => (
          <View key={intro.id} style={[styles.introCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
            <View style={styles.introHeader}>
              <ThemedText style={[styles.introLabel, { color: theme.textSecondary }]}>
                Intro for
              </ThemedText>
              <View style={[
                styles.statusBadge,
                {
                  backgroundColor: intro.status === 'accepted'
                    ? '#10B981'
                    : intro.status === 'rejected'
                    ? '#EF4444'
                    : theme.textTertiary,
                }
              ]}>
                <ThemedText style={styles.statusText}>
                  {intro.status.charAt(0).toUpperCase() + intro.status.slice(1)}
                </ThemedText>
              </View>
            </View>
            {intro.request && (
              <ThemedText style={[styles.introBusinessName, { color: theme.text }]}>
                {intro.request.businessName}
              </ThemedText>
            )}
            <View style={styles.introDetail}>
              <Ionicons name="person-outline" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.introDetailText, { color: theme.text }]}>
                {intro.contactName}
              </ThemedText>
            </View>
            <View style={styles.introDetail}>
              <Ionicons name="call-outline" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.introDetailText, { color: theme.text }]}>
                {intro.contactPhone}
              </ThemedText>
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  const renderLeaderboard = () => {
    if (loading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      );
    }

    if (leaderboard.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Ionicons name="trophy-outline" size={64} color={theme.textTertiary} />
          <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
            No points earned yet this month
          </ThemedText>
          <ThemedText style={[styles.emptySubtext, { color: theme.textTertiary }]}>
            Start helping members to earn points!
          </ThemedText>
        </View>
      );
    }

    return (
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.accent} />
        }
      >
        {leaderboard.map((entry) => (
          <View
            key={entry.userId}
            style={[styles.leaderboardCard, { backgroundColor: theme.card, borderColor: theme.border }]}
          >
            <View style={styles.leaderboardRank}>
              <View style={[
                styles.rankBadge,
                {
                  backgroundColor: entry.rank <= 3
                    ? entry.rank === 1
                      ? '#FFD700'
                      : entry.rank === 2
                      ? '#C0C0C0'
                      : '#CD7F32'
                    : theme.border,
                }
              ]}>
                <ThemedText style={[styles.rankText, { color: entry.rank <= 3 ? '#000' : theme.text }]}>
                  {entry.rank}
                </ThemedText>
              </View>
            </View>
            <View style={styles.leaderboardUser}>
              {entry.user.photo ? (
                <Image
                  source={{ uri: entry.user.photo }}
                  style={styles.leaderboardAvatar}
                  contentFit="cover"
                />
              ) : (
                <View style={[styles.leaderboardAvatarPlaceholder, { backgroundColor: theme.accent + '20' }]}>
                  <Ionicons name="person" size={20} color={theme.accent} />
                </View>
              )}
              <View style={styles.leaderboardUserInfo}>
                <ThemedText style={[styles.leaderboardName, { color: theme.text }]}>
                  {entry.user.name || 'Anonymous'}
                </ThemedText>
                {entry.user.businessName && (
                  <ThemedText style={[styles.leaderboardBusiness, { color: theme.textSecondary }]}>
                    {entry.user.businessName}
                  </ThemedText>
                )}
              </View>
            </View>
            <View style={styles.leaderboardPoints}>
              <Ionicons name="star" size={20} color="#FFD700" />
              <ThemedText style={[styles.pointsText, { color: theme.accent }]}>
                {entry.points}
              </ThemedText>
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  const renderContent = () => {
    switch (activeSubTab) {
      case 'requests':
        return renderRequests();
      case 'my-requests':
        return renderMyRequests();
      case 'my-intros':
        return renderMyIntros();
      case 'leaderboard':
        return renderLeaderboard();
      default:
        return null;
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      {renderSubTabs()}
      
      {/* Posting Restriction Banner */}
      {!isPostingAllowedToday && allowedDayNames && (
        <View style={[styles.warningBanner, { backgroundColor: theme.accent + '15', borderColor: theme.accent + '30' }]}>
          <Ionicons name="calendar-outline" size={20} color={theme.accent} />
          <ThemedText style={[styles.warningText, { color: theme.accent }]}>
            Referral posting only allowed on: {allowedDayNames}
          </ThemedText>
        </View>
      )}
      
      {renderContent()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 8,
    borderRadius: 8,
    borderWidth: 1,
    gap: 10,
  },
  warningText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
  },
  subTabContainer: {
    borderBottomWidth: 1,
  },
  subTabScroll: {
    flexDirection: 'row',
  },
  subTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
    gap: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  subTabLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  subTabLabelActive: {
    fontWeight: '700',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 32,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    marginTop: 16,
    gap: 8,
  },
  createButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  statsBar: {
    flexDirection: 'row',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    marginBottom: 8,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statDivider: {
    width: 1,
    marginHorizontal: 8,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 11,
    textAlign: 'center',
  },
  responseBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginTop: -8,
    marginBottom: 12,
    gap: 6,
  },
  responseBadgeText: {
    fontSize: 13,
    fontWeight: '600',
  },
  introCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  introHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  introLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFF',
  },
  introBusinessName: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  introDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 6,
  },
  introDetailText: {
    fontSize: 14,
  },
  leaderboardCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  leaderboardRank: {
    marginRight: 16,
  },
  rankBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rankText: {
    fontSize: 16,
    fontWeight: '700',
  },
  leaderboardUser: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  leaderboardAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  leaderboardAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leaderboardUserInfo: {
    marginLeft: 12,
    flex: 1,
  },
  leaderboardName: {
    fontSize: 15,
    fontWeight: '600',
  },
  leaderboardBusiness: {
    fontSize: 13,
    marginTop: 2,
  },
  leaderboardPoints: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  pointsText: {
    fontSize: 18,
    fontWeight: '700',
  },
});

export default ClubReferralsTab;
